import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, IconButton, TextField, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';

import SortableList from './SortableList';

const groupSummary = (group) => {
  if (!group) {
    return 'This group no longer exists';
  }
  const count = (group.options || []).length;
  const rule = group.required ? `Required${group.force_min ? `, min ${group.force_min}` : ''}` : 'Optional';
  return `${rule}${group.force_max ? `, max ${group.force_max}` : ''} · ${count} choice${count === 1 ? '' : 's'}`;
};

/** Choice groups attached to a dish or one of its sizes. Changes save straight away. */
const ChoiceGroupsField = ({ title, description, attached, groups, onAdd, onRemove, onReorder, busy, dragType }) => {
  const [inputValue, setInputValue] = useState('');
  const byId = new Map(groups.map((group) => [group.id, group]));
  const attachedIds = new Set((attached || []).map((ref) => ref.id));
  const available = groups.filter((group) => !attachedIds.has(group.id));

  const row = (ref, drag) => {
    const group = byId.get(ref.id);
    return (
      <Box
        ref={drag && drag.rowRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.75,
          mb: 0.75,
          border: '1px solid',
          borderColor: group ? 'divider' : 'error.light',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          opacity: drag && drag.isDragging ? 0.4 : 1,
        }}
      >
        {drag && (
          <Box ref={drag.handleRef} sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab' }} aria-label="Drag to reorder">
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {group ? group.name : 'No name'}
          </Typography>
          <Typography variant="caption" color={group ? 'text.secondary' : 'error'}>
            {groupSummary(group)}
          </Typography>
        </Box>
        <IconButton size="small" aria-label={`Remove ${group ? group.name : 'group'}`} onClick={() => onRemove(ref.id)} disabled={busy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      )}
      {(attached || []).length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
          None attached.
        </Typography>
      )}
      {onReorder ? (
        <SortableList type={dragType} items={attached || []} getKey={(ref) => ref.id} onReorder={onReorder} disabled={busy} renderItem={(ref, index, drag) => row(ref, drag)} />
      ) : (
        (attached || []).map((ref) => <React.Fragment key={ref.id}>{row(ref)}</React.Fragment>)
      )}
      <Autocomplete
        size="small"
        options={available}
        getOptionLabel={(group) => group.name || 'No name'}
        renderOption={(props, group) => (
          <li {...props} key={group.id}>
            <Box>
              <Typography variant="body2">{group.name || 'No name'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {groupSummary(group)}
              </Typography>
            </Box>
          </li>
        )}
        value={null}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => setInputValue(reason === 'reset' ? '' : value)}
        onChange={(event, group) => {
          if (group) {
            onAdd(group.id);
          }
        }}
        disabled={busy}
        noOptionsText="No more groups to add"
        renderInput={(params) => <TextField {...params} placeholder="Add a choice group…" />}
      />
    </Box>
  );
};

ChoiceGroupsField.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  attached: PropTypes.array,
  groups: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  busy: PropTypes.bool,
  dragType: PropTypes.string,
};

export default ChoiceGroupsField;
